import { Link, Tile, twMerge } from '@sortlist-frontend/design-system';
import { Check, ChevronDown, TriangleAlert } from '@sortlist-frontend/design-system/icons';
import { Fragment } from 'react';

import type { RankingAuthor, RankingBlock, RankingListStyle, RankingRichText } from '../types';
import { agencyProfilePath } from '../utils/agencyProfilePath';
import { richTextToPlainText } from '../utils/richText';
import { BarChart } from './BarChart';
import { CompactAgencyCard } from './CompactAgencyCard';
import { EditorialImage } from './EditorialImage';
import { EditorialTable } from './EditorialTable';
import { FullRankingBlock } from './FullRanking/FullRankingBlock';
import { PromisingAgencyCard } from './FullRanking/PromisingAgencyCard';
import { ListingPagesNav } from './ListingPagesNav';
import { RankingAuthorCard } from './RankingAuthorCard';
import { RankingCard } from './RankingCard';
import { RichText } from './RichText';
import { ScatterChart } from './ScatterChart';

interface EditorialBlocksProps {
  blocks: readonly RankingBlock[];
  author?: RankingAuthor;
  locale: string;
  serviceName: string;
  path: string;
}

interface EditorialListProps {
  style: RankingListStyle;
  items: readonly RankingRichText[];
}

const EditorialList = (props: EditorialListProps) => {
  const { style, items } = props;

  if (style === 'number') {
    return (
      <ol className="list-outside list-decimal space-y-3 pl-5 leading-relaxed text-secondary-700 marker:font-semibold marker:text-primary-600">
        {items.map((item) => (
          <li key={richTextToPlainText(item)}>
            <RichText text={item} />
          </li>
        ))}
      </ol>
    );
  }

  return (
    <ul className="space-y-3 leading-relaxed text-secondary-700">
      {items.map((item) => (
        <li key={richTextToPlainText(item)} className="flex items-start gap-3">
          {style === 'warning' ? (
            <TriangleAlert size={18} className="mt-1 shrink-0 text-danger-500" aria-hidden="true" />
          ) : (
            <Tile size="2xs" color="success" variant="flat" radius="full" className="mt-1">
              <Check />
            </Tile>
          )}
          <span>
            <RichText text={item} />
          </span>
        </li>
      ))}
    </ul>
  );
};

export const EditorialBlocks = (props: EditorialBlocksProps) => {
  const { blocks, author, locale, serviceName, path } = props;

  return (
    <div className="space-y-6">
      {blocks.map((block, index) => {
        const key = `${index}-${block.type}`;

        switch (block.type) {
          case 'paragraph':
            return (
              <p key={key} className="leading-relaxed text-secondary-700">
                <RichText text={block.text} />
              </p>
            );
          case 'subheading':
            return (
              <h3 key={key} className="pt-4 text-xl font-semibold text-secondary-950">
                {block.text}
              </h3>
            );
          case 'cards':
            return (
              <div key={key} className="grid gap-4 md:grid-cols-2">
                {block.items.map((item, itemIndex) => (
                  <RankingCard
                    key={item.title}
                    className={twMerge(
                      block.items.length % 2 === 1 && itemIndex === block.items.length - 1 && 'md:col-span-2',
                    )}>
                    <h3 className="text-lg font-semibold text-secondary-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-secondary-700">
                      <RichText text={item.text} />
                    </p>
                  </RankingCard>
                ))}
              </div>
            );
          case 'steps':
            return (
              <ol key={key} className="space-y-5">
                {block.items.map((step, stepIndex) => (
                  <li key={step.title} className="flex items-start gap-4">
                    <Tile size="md" color="primary" variant="solid" radius="full" className="mt-0.5 font-semibold">
                      {stepIndex + 1}
                    </Tile>
                    <div>
                      <p className="font-semibold text-secondary-950">{step.title}</p>
                      <p className="mt-1 leading-relaxed text-secondary-700">{step.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            );
          case 'list':
            return <EditorialList key={key} style={block.style} items={block.items} />;
          case 'lists':
            return (
              <div key={key} className="grid gap-8 md:grid-cols-2">
                {block.groups.map((group) => (
                  <div key={group.title}>
                    <h3 className="mb-4 text-xl font-semibold text-secondary-950">{group.title}</h3>
                    <EditorialList style={group.style} items={group.items} />
                  </div>
                ))}
              </div>
            );
          case 'table':
            return <EditorialTable key={key} caption={block.caption} columns={block.columns} rows={block.rows} />;
          case 'chart':
            return (
              <RankingCard key={key}>
                <BarChart chart={block.chart} locale={locale} />
              </RankingCard>
            );
          case 'agencies':
            return (
              <ul key={key} className="grid gap-4 md:grid-cols-2">
                {block.items.map((agency) => (
                  <li key={agency.slug}>
                    <CompactAgencyCard agency={agency} locale={locale} />
                  </li>
                ))}
              </ul>
            );
          case 'agencyGroups':
            return (
              <div key={key} className="grid gap-4 md:grid-cols-2">
                {block.groups.map((group) => (
                  <RankingCard key={group.title}>
                    <h3 className="text-lg font-semibold text-secondary-950">{group.title}</h3>
                    <ol className="mt-3 space-y-2 text-sm text-secondary-700">
                      {group.items.map((item) => (
                        <li key={item.slug} className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-semibold text-primary-600">#{item.rank}</span>
                          <Link href={agencyProfilePath(item.slug)} color="foreground" underline="always">
                            {item.name}
                          </Link>
                          <span className="text-secondary-500">{item.note}</span>
                        </li>
                      ))}
                    </ol>
                  </RankingCard>
                ))}
              </div>
            );
          case 'promisingAgencies':
            return (
              <ul key={key} className="grid gap-4 md:grid-cols-2">
                {block.items.map((agency) => (
                  <li key={agency.slug}>
                    <PromisingAgencyCard agency={agency} locale={locale} />
                  </li>
                ))}
              </ul>
            );
          case 'image':
            return <EditorialImage key={key} image={block.image} />;
          case 'scatterChart':
            return (
              <RankingCard key={key}>
                <ScatterChart chart={block.chart} locale={locale} />
              </RankingCard>
            );
          case 'disclosure':
            return (
              <details key={key} className="group rounded-2xl border border-secondary-200 bg-white shadow-xs">
                <summary className="flex cursor-pointer list-none items-center gap-2 p-5 font-semibold text-secondary-950 [&::-webkit-details-marker]:hidden">
                  <ChevronDown
                    size={18}
                    className="shrink-0 transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                  {block.title}
                </summary>
                <div className="border-t border-secondary-200 p-5">
                  <EditorialBlocks
                    blocks={block.blocks}
                    author={author}
                    locale={locale}
                    serviceName={serviceName}
                    path={path}
                  />
                </div>
              </details>
            );
          case 'listingNav':
            return (
              <ListingPagesNav
                key={key}
                totalAgenciesCount={block.totalAgenciesCount}
                pages={block.pages}
                serviceName={serviceName}
                path={path}
                locale={locale}
              />
            );
          case 'fullRanking':
            return (
              <FullRankingBlock
                key={key}
                title={block.title}
                intro={block.intro}
                tableCaption={block.tableCaption}
                entries={block.entries}
                filters={block.filters}
                summaryLabel={block.summaryLabel}
                locale={locale}
              />
            );
          case 'quote':
            return (
              <blockquote
                key={key}
                className="border-l-4 border-primary-200 pl-5 italic leading-relaxed text-secondary-700">
                <RichText text={block.text} />
                {block.cite != null ? (
                  <cite className="mt-2 block text-sm not-italic text-secondary-500">{block.cite}</cite>
                ) : null}
              </blockquote>
            );
          case 'author':
            return author != null ? <RankingAuthorCard key={key} author={author} /> : <Fragment key={key} />;
        }
      })}
    </div>
  );
};
